import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';

const ApprovalHierarchy = ({
  selectedType,
  selectedHazards,
  approvalLevels,
  onApprovalLevelsChange,
  errors
}) => {
  const [criticalityLevel, setCriticalityLevel] = useState('low');
  const [requiredLevels, setRequiredLevels] = useState(2);

  const approvers = [
  {
    value: 'john-smith',
    label: 'John Smith',
    role: 'Electrical Supervisor',
    department: 'Electrical',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_17fe719dd-1763292172012.png",
    avatarAlt: 'Professional headshot of Caucasian male supervisor with short brown hair wearing blue work uniform and safety glasses'
  },
  {
    value: 'maria-garcia',
    label: 'Maria Garcia',
    role: 'Safety Officer',
    department: 'Safety',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_18f8e9b9e-1763297225913.png",
    avatarAlt: 'Professional headshot of Hispanic female safety officer with long dark hair in high visibility vest and hard hat'
  },
  {
    value: 'david-chen',
    label: 'David Chen',
    role: 'Operations Manager',
    department: 'Operations',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_12ad85e75-1763293797967.png",
    avatarAlt: 'Professional headshot of Asian male operations manager with black hair wearing gray work shirt and safety equipment'
  },
  {
    value: 'sarah-johnson',
    label: 'Sarah Johnson',
    role: 'Plant Manager',
    department: 'Management',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1a2d8d9d7-1763297341060.png",
    avatarAlt: 'Professional headshot of Caucasian female plant manager with blonde hair in white hard hat and blue work jacket'
  },
  {
    value: 'robert-williams',
    label: 'Robert Williams',
    role: 'HSE Manager',
    department: 'Safety',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1c7c570ac-1763296079697.png",
    avatarAlt: 'Professional headshot of African American male HSE manager with short hair wearing protective gear and safety vest'
  },
  {
    value: 'emily-davis',
    label: 'Emily Davis',
    role: 'Site Director',
    department: 'Executive',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_129aa8ebc-1763299616245.png",
    avatarAlt: 'Professional headshot of Caucasian female site director with brown hair in professional business attire'
  }];


  // Calculate criticality based on permit type and hazards
  useEffect(() => {
    const calculateCriticality = () => {
      let criticalityScore = 0;

      // High-risk permit types
      const highRiskPermits = ['hot-work', 'confined-space', 'electrical'];
      const mediumRiskPermits = ['height-work', 'excavation'];

      if (highRiskPermits?.includes(selectedType)) {
        criticalityScore += 3;
      } else if (mediumRiskPermits?.includes(selectedType)) {
        criticalityScore += 2;
      } else {
        criticalityScore += 1;
      }

      // High-risk hazards
      const highRiskHazards = ['toxic', 'flammable', 'shock', 'arc-flash', 'corrosive'];
      const mediumRiskHazards = ['noise', 'temperature', 'pressure', 'moving-parts'];

      const highRiskCount = selectedHazards?.filter((h) => highRiskHazards?.includes(h))?.length || 0;
      const mediumRiskCount = selectedHazards?.filter((h) => mediumRiskHazards?.includes(h))?.length || 0;

      if (highRiskCount >= 2) {
        criticalityScore += 3;
      } else if (highRiskCount === 1) {
        criticalityScore += 2;
      } else if (mediumRiskCount >= 2) {
        criticalityScore += 1;
      }

      // Determine criticality level and required approval levels
      let level = 'low';
      let levels = 2;

      if (criticalityScore >= 5) {
        level = 'critical';
        levels = 4; // All 4 levels required
      } else if (criticalityScore >= 3) {
        level = 'high';
        levels = 3; // 3 levels required
      } else if (criticalityScore >= 2) {
        level = 'medium';
        levels = 2; // 2 levels required (issuer + acceptor)
      } else {
        level = 'low';
        levels = 2; // 2 levels required (issuer + acceptor)
      }

      setCriticalityLevel(level);
      setRequiredLevels(levels);

      // Initialize approval levels if not already set
      if (!approvalLevels || approvalLevels?.length === 0) {
        const initialLevels = [];
        for (let i = 0; i < levels; i++) {
          initialLevels?.push({
            level: i + 1,
            approver: '',
            status: 'pending'
          });
        }
        onApprovalLevelsChange(initialLevels);
      }
    };

    if (selectedType) {
      calculateCriticality();
    }
  }, [selectedType, selectedHazards]);

  const getCriticalityColor = () => {
    switch (criticalityLevel) {
      case 'critical':
        return 'text-error bg-error/10 border-error';
      case 'high':
        return 'text-warning bg-warning/10 border-warning';
      case 'medium':
        return 'text-info bg-info/10 border-info';
      default:
        return 'text-success bg-success/10 border-success';
    }
  };

  const getCriticalityIcon = () => {
    switch (criticalityLevel) {
      case 'critical':
        return 'AlertTriangle';
      case 'high':
        return 'AlertCircle';
      case 'medium':
        return 'Info';
      default:
        return 'CheckCircle';
    }
  };

  const getLevelName = (level) => {
    switch (level) {
      case 1:
        return 'Permit Issuer';
      case 2:
        return 'Permit Acceptor';
      case 3:
        return 'Safety Review';
      case 4:
        return 'Management Approval';
      default:
        return `Level ${level}`;
    }
  };

  const getLevelDescription = (level) => {
    switch (level) {
      case 1:
        return 'Reviews and approves the technical aspects of the work permit';
      case 2:
        return 'Accepts the permit and confirms readiness to execute the work';
      case 3:
        return 'Reviews safety measures and hazard controls (required for high-risk work)';
      case 4:
        return 'Final management approval (required for critical work)';
      default:
        return '';
    }
  };

  const handleApproverChange = (level, approverId) => {
    const updatedLevels = approvalLevels?.map((l) =>
    l?.level === level ? { ...l, approver: approverId } : l
    );
    onApprovalLevelsChange(updatedLevels);
  };

  return (
    <div className="bg-card rounded-xl shadow-sm border border-border">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="GitBranch" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Approval Hierarchy</h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Assign approvers based on work criticality
              </p>
            </div>
          </div>

          {/* Criticality Badge */}
          <div className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${getCriticalityColor()}`}>
            <Icon name={getCriticalityIcon()} size={18} />
            <div>
              <p className="text-xs uppercase font-semibold">Criticality</p>
              <p className="text-sm font-bold capitalize">{criticalityLevel}</p>
            </div>
          </div>
        </div>

        {/* Criticality Explanation */}
        <div className="mt-4 p-4 bg-muted/50 rounded-lg">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={18} className="text-info flex-shrink-0 mt-0.5" />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">
                {requiredLevels} approval levels required for this permit
              </p>
              <p>
                Based on the permit type ({selectedType || 'not selected'}) and identified hazards, 
                this work requires <strong className="text-foreground">{requiredLevels} levels of approval</strong>.
                {criticalityLevel === 'critical' && ' This is classified as critical work requiring full approval chain.'}
                {criticalityLevel === 'high' && ' This is high-risk work requiring additional safety review.'}
                {criticalityLevel === 'medium' && ' This is medium-risk work requiring standard approval process.'}
                {criticalityLevel === 'low' && ' This is low-risk work requiring basic approval process.'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-4">
        {/* Approval Levels */}
        {approvalLevels?.map((level, index) =>
        <div
          key={level?.level}
          className="relative pl-8 pb-6 last:pb-0">

            {/* Timeline connector */}
            {index < approvalLevels?.length - 1 &&
          <div className="absolute left-[15px] top-8 bottom-0 w-0.5 bg-border" />
          }

            {/* Level indicator */}
            <div className="absolute left-0 top-1 flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 border-2 border-primary text-primary font-semibold text-sm">
              {level?.level}
            </div>

            <div className="space-y-3">
              <div>
                <h4 className="font-semibold text-foreground">{getLevelName(level?.level)}</h4>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {getLevelDescription(level?.level)}
                </p>
              </div>

              {/* Approver Selection */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Select Approver <span className="text-error">*</span>
                </label>
                <Select
                value={level?.approver}
                onChange={(value) => handleApproverChange(level?.level, value)}
                placeholder="Choose an approver"
                options={approvers?.map((approver) => ({
                  value: approver?.value,
                  label:
                  <div className="flex items-center gap-3">
                        <img
                      src={approver?.avatar}
                      alt={approver?.avatarAlt}
                      className="w-8 h-8 rounded-full object-cover" />

                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-foreground truncate">{approver?.label}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {approver?.role} • {approver?.department}
                          </p>
                        </div>
                      </div>

                }))} />

                {errors?.[`approver_level_${level?.level}`] &&
              <p className="text-sm text-error mt-1 flex items-center gap-1">
                    <Icon name="AlertCircle" size={14} />
                    {errors?.[`approver_level_${level?.level}`]}
                  </p>
              }
              </div>

              {/* Selected Approver Display */}
              {level?.approver &&
            <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg border border-border">
                  <img
                src={approvers?.find((a) => a?.value === level?.approver)?.avatar}
                alt={approvers?.find((a) => a?.value === level?.approver)?.avatarAlt}
                className="w-10 h-10 rounded-full object-cover" />

                  <div className="flex-1">
                    <p className="font-medium text-foreground">
                      {approvers?.find((a) => a?.value === level?.approver)?.label}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {approvers?.find((a) => a?.value === level?.approver)?.role}
                    </p>
                  </div>
                  <div className="px-3 py-1 bg-warning/10 text-warning text-xs font-semibold rounded-full">
                    Pending
                  </div>
                </div>
            }
            </div>
          </div>
        )}

        {/* Info Box */}
        <div className="mt-6 p-4 bg-info/5 border border-info/20 rounded-lg">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={18} className="text-info flex-shrink-0 mt-0.5" />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">Approval Process</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Approvals must be completed in sequence from Level 1 to Level {requiredLevels}</li>
                <li>Each approver will receive a notification when their approval is required</li>
                <li>Work cannot begin until all required approvals are obtained</li>
                <li>Any approver can reject the permit, requiring resubmission</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>);

};

export default ApprovalHierarchy;